'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

// =============================================================================
// Global image cache — persists for the entire SPA session across navigations.
// Shared by SeatCell, AvatarWithFlair, and any future consumer that
// needs fade-in image loading without re-downloads on revisit.
// =============================================================================

const loadedImages = new Set<string>();

/**
 * Hook that tracks image loading state with a fade-in transition.
 * Returns `loaded` = true once the image is ready (or was previously cached).
 */
export function useImageLoaded(src: string | undefined): {
  loaded: boolean;
  onLoad: () => void;
  onError: () => void;
  failed: boolean;
} {
  const alreadyCached = src ? loadedImages.has(src) : false;
  const [loaded, setLoaded] = useState(alreadyCached);
  const [failed, setFailed] = useState(false);

  // Reset when src changes
  useEffect(() => {
    if (!src) {
      setLoaded(false);
      setFailed(false);
      return;
    }
    if (loadedImages.has(src)) {
      setLoaded(true);
      setFailed(false);
      return;
    }
    setLoaded(false);
    setFailed(false);
  }, [src]);

  const onLoad = useCallback(() => {
    if (src) loadedImages.add(src);
    setLoaded(true);
  }, [src]);

  const onError = useCallback(() => {
    setFailed(true);
  }, []);

  return { loaded, onLoad, onError, failed };
}

/**
 * Check whether a URL is already in the global image cache.
 */
export function isImageCached(src: string): boolean {
  return loadedImages.has(src);
}

/**
 * Batch-preload an array of image URLs in the background.
 * Uses `new Image()` to trigger browser fetches; each successful load is
 * added to the global cache so `useImageLoaded` instantly returns loaded=true.
 *
 * - Skips URLs that are already cached.
 * - Skips data: URIs (they are inline and always "loaded").
 * - Fires and forgets — does not block rendering.
 */
export function preloadImages(urls: (string | null | undefined)[]): void {
  for (const url of urls) {
    if (!url) continue;
    // data: URIs are synchronous; mark cached and skip network fetch
    if (url.startsWith('data:')) {
      loadedImages.add(url);
      continue;
    }
    if (loadedImages.has(url)) continue;

    const img = new Image();
    img.onload = () => {
      loadedImages.add(url);
    };
    // On error, do nothing — useImageLoaded will detect failure per-component
    img.src = url;
  }
}

/**
 * Hook that preloads images AND triggers a re-render when they finish loading.
 * Unlike bare `preloadImages`, this ensures components see updated `isImageCached`
 * results without requiring an unrelated interaction (like hover) to re-render.
 */
export function usePreloadImages(urls: (string | null | undefined)[]): void {
  const [, setTick] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const pending: string[] = [];
    for (const url of urls) {
      if (!url) continue;
      if (url.startsWith('data:')) {
        loadedImages.add(url);
        continue;
      }
      if (!loadedImages.has(url)) pending.push(url);
    }
    if (pending.length === 0) return;

    for (const url of pending) {
      if (loadedImages.has(url)) continue;
      const img = new Image();
      img.onload = () => {
        loadedImages.add(url);
        if (mountedRef.current) setTick(t => t + 1);
      };
      img.src = url;
    }

    return () => { mountedRef.current = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urls.length, urls[0], urls[urls.length - 1]]);
}
